import { 
  TrendingUp, 
  BarChart3, 
  Link2, 
  MousePointerClick, 
  Target, 
  DollarSign, 
  Percent, 
  Calculator,
  Users,
  Heart,
  Hash,
  FileText,
  Type,
  Palette,
  Smile,
  ShoppingCart,
  LucideIcon
} from "lucide-react";

export interface Tool {
  title: string;
  description: string;
  metaDescription: string;
  href: string;
  slug: string;
  icon: LucideIcon;
  category: ToolCategory;
  available: boolean;
}

export type ToolCategory = 
  | "marketing-calculators"
  | "seo-content"
  | "analytics-tracking"
  | "business-retention"
  | "utilities";

export const categoryLabels: Record<ToolCategory, { title: string; description: string }> = {
  "marketing-calculators": {
    title: "Marketing & Paid Media Calculators",
    description: "Measure campaign performance and optimize your advertising spend."
  },
  "seo-content": {
    title: "SEO & Content Tools",
    description: "Optimize your content for search engines and readability."
  },
  "analytics-tracking": {
    title: "Analytics & Tracking",
    description: "Track conversions and attribute marketing success."
  },
  "business-retention": {
    title: "Business & Retention",
    description: "Analyze customer value and retention metrics."
  },
  "utilities": {
    title: "Utilities",
    description: "Helpful tools for everyday marketing tasks."
  }
};

export const tools: Tool[] = [
  // Marketing & Paid Media Calculators
  {
    title: "ROI Calculator",
    description: "Calculate return on investment for your marketing initiatives.",
    metaDescription: "Calculate your Return on Investment (ROI) to measure the profitability of marketing campaigns. Understand the true financial impact of your marketing spend.",
    href: "/tools/roi-calculator",
    slug: "roi-calculator",
    icon: TrendingUp,
    category: "marketing-calculators",
    available: true,
  },
  {
    title: "ROAS Calculator",
    description: "Calculate your Return on Ad Spend and optimize your marketing budget.",
    metaDescription: "Calculate Return on Ad Spend (ROAS) to measure advertising efficiency. Understand how much revenue you generate for every dollar spent on ads.",
    href: "/tools/roas-calculator",
    slug: "roas-calculator",
    icon: BarChart3,
    category: "marketing-calculators",
    available: true,
  },
  {
    title: "CTR Calculator",
    description: "Calculate click-through rates to measure ad performance.",
    metaDescription: "Calculate Click-Through Rate (CTR) to measure how effectively your ads drive user engagement. A critical metric for ad optimization.",
    href: "/tools/ctr-calculator",
    slug: "ctr-calculator",
    icon: MousePointerClick,
    category: "marketing-calculators",
    available: true,
  },
  {
    title: "CPA Calculator",
    description: "Calculate cost per acquisition for your marketing campaigns.",
    metaDescription: "Calculate Cost Per Acquisition (CPA) to understand how much you spend to acquire each new customer. Essential for budget optimization.",
    href: "/tools/cpa-calculator",
    slug: "cpa-calculator",
    icon: Target,
    category: "marketing-calculators",
    available: true,
  },
  {
    title: "CPC Calculator",
    description: "Calculate cost per click for your paid advertising campaigns.",
    metaDescription: "Calculate Cost Per Click (CPC) to optimize your paid advertising budget. Understand the efficiency of your click-based campaigns.",
    href: "/tools/cpc-calculator",
    slug: "cpc-calculator",
    icon: DollarSign,
    category: "marketing-calculators",
    available: true,
  },
  {
    title: "CPM Calculator",
    description: "Calculate cost per thousand impressions for your campaigns.",
    metaDescription: "Calculate Cost Per Mille (CPM) to measure the cost of reaching 1,000 people with your ads. Essential for brand awareness campaigns.",
    href: "/tools/cpm-calculator",
    slug: "cpm-calculator",
    icon: Percent,
    category: "marketing-calculators",
    available: true,
  },
  
  // Analytics & Tracking
  {
    title: "Conversion Rate Calculator",
    description: "Calculate your conversion rates and track improvements over time.",
    metaDescription: "Calculate your website or campaign conversion rate. Measure the percentage of visitors who take a desired action to optimize your funnel.",
    href: "/tools/conversion-calculator",
    slug: "conversion-calculator",
    icon: Calculator,
    category: "analytics-tracking",
    available: true,
  },
  {
    title: "UTM Builder",
    description: "Create and manage UTM parameters for tracking campaign performance.",
    metaDescription: "Build UTM-tagged URLs to track your marketing campaigns in Google Analytics. Essential for multi-channel attribution.",
    href: "/tools/utm-builder",
    slug: "utm-builder",
    icon: Link2,
    category: "analytics-tracking",
    available: true,
  },
  
  // Business & Retention
  {
    title: "Churn Rate Calculator",
    description: "Analyze customer retention and calculate your churn rate.",
    metaDescription: "Calculate your customer churn rate to measure retention health. Understanding churn is critical for sustainable business growth.",
    href: "/tools/churn-calculator",
    slug: "churn-calculator",
    icon: Users,
    category: "business-retention",
    available: true,
  },
  {
    title: "Customer Lifetime Value Calculator",
    description: "Calculate the total value a customer brings to your business.",
    metaDescription: "Calculate Customer Lifetime Value (CLV/LTV) to understand the total revenue a customer generates over their relationship with your business.",
    href: "/tools/clv-calculator",
    slug: "clv-calculator",
    icon: Heart,
    category: "business-retention",
    available: true,
  },
  {
    title: "NPS Calculator",
    description: "Calculate your Net Promoter Score to measure customer satisfaction.",
    metaDescription: "Calculate Net Promoter Score (NPS) to measure customer loyalty and satisfaction. A key metric for predicting business growth.",
    href: "/tools/nps-calculator",
    slug: "nps-calculator",
    icon: Users,
    category: "business-retention",
    available: true,
  },
  
  // SEO & Content Tools
  {
    title: "Keyword Density Calculator",
    description: "Analyze keyword density to optimize your content for SEO.",
    metaDescription: "Calculate keyword density in your content to optimize for SEO without over-optimization. Find the right balance for search visibility.",
    href: "/tools/keyword-density",
    slug: "keyword-density",
    icon: Hash,
    category: "seo-content",
    available: true,
  },
  {
    title: "Readability Checker",
    description: "Analyze and improve the readability of your content.",
    metaDescription: "Check the readability of your content using Flesch-Kincaid and other scores. Ensure your content is accessible to your target audience.",
    href: "/tools/readability-checker",
    slug: "readability-checker",
    icon: FileText,
    category: "seo-content",
    available: true,
  },
  {
    title: "Lorem Ipsum Generator",
    description: "Generate placeholder text for your designs and mockups.",
    metaDescription: "Generate Lorem Ipsum placeholder text for your designs, mockups, and prototypes. Customize paragraph count and length.",
    href: "/tools/lorem-ipsum",
    slug: "lorem-ipsum",
    icon: Type,
    category: "utilities",
    available: true,
  },
  
  // Utilities
  {
    title: "Color Picker",
    description: "Find and explore beautiful colors for your designs.",
    metaDescription: "Pick and convert colors between HEX, RGB, and HSL formats. Generate color palettes for your design projects.",
    href: "/tools/color-picker",
    slug: "color-picker",
    icon: Palette,
    category: "utilities",
    available: true,
  },
  {
    title: "Emoji Cheat Sheet",
    description: "Browse and copy emojis easily for your content.",
    metaDescription: "Browse, search, and copy emojis for your social media posts, emails, and content marketing. Organized by category.",
    href: "/tools/emoji-cheatsheet",
    slug: "emoji-cheatsheet",
    icon: Smile,
    category: "utilities",
    available: true,
  },
  {
    title: "Amazon Sales Calculator",
    description: "Calculate your Amazon sales and fee breakdowns.",
    metaDescription: "Calculate Amazon FBA fees and profit margins. Understand your true profitability when selling on Amazon Marketplace.",
    href: "/tools/amazon-calculator",
    slug: "amazon-calculator",
    icon: ShoppingCart,
    category: "utilities",
    available: true,
  },
];

export const getToolsByCategory = () => {
  const categories: ToolCategory[] = [
    "marketing-calculators",
    "analytics-tracking",
    "business-retention",
    "seo-content",
    "utilities"
  ];
  
  return categories.map(category => ({
    ...categoryLabels[category],
    category,
    tools: tools.filter(t => t.category === category && t.available)
  })).filter(c => c.tools.length > 0);
};

export const getRelatedTools = (currentSlug: string, limit = 2): Tool[] => {
  const current = tools.find(t => t.slug === currentSlug);
  if (!current) return [];
  
  // First, get tools from same category
  const sameCategory = tools.filter(
    t => t.category === current.category && t.slug !== currentSlug && t.available
  );
  
  // Then get from other categories
  const otherCategories = tools.filter(
    t => t.category !== current.category && t.slug !== currentSlug && t.available
  );
  
  return [...sameCategory, ...otherCategories].slice(0, limit);
};
